import React, {useState} from 'react';
import clsx from 'clsx';
import * as Yup from 'yup';
import {FormikProps, useFormik} from 'formik';
import {useIntl} from 'react-intl';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';
import LoginAsCustomerModal from '../../../app/pages/admin/loginAsCustomer/LoginAsCustomerModal';
import {CustomerScope, LoginAsCustomerFormValues} from '../../../app/interfaces/login';
import {
  useCurrentCustomer,
  usePermissions,
} from '../../../app/modules/auth/authorization/PermissionProvider';
import {checkIfAuthorized} from '../../../app/modules/auth/authorization/api';
import {useCustomerList} from '../../../app/hooks/useCustomerManagerHook';
import {useClearCurrentCustomer, useSetCurrentCustomer} from '../Hooks/UserManager';
import {CustomerSystem} from '../Types/customer';

interface CustomerScopeSwitcherProps {
  className?: string;
}

const CustomerScopeSwitcher = ({className}: CustomerScopeSwitcherProps): JSX.Element | null => {
  const intl = useIntl();
  const [show, setShow] = useState(false);
  const currentCustomer = useCurrentCustomer();
  const userPermissions = usePermissions();
  const {data: customers = []} = useCustomerList();
  const {mutate: setCurrentCustomer, isLoading: isSetting} = useSetCurrentCustomer();
  const {mutate: clearCurrentCustomer, isLoading: isClearing} = useClearCurrentCustomer();

  const isAdmin = checkIfAuthorized(userPermissions, ['ecAdmin']);

  const validationRules = Yup.object().shape({
    scope: Yup.mixed<CustomerScope>().oneOf(['all', 'one']).required(),
    client_id: Yup.string().when('scope', {
      is: 'one',
      then: Yup.string().required(intl.formatMessage({id: 'FIELD.REQUIRED'})),
    }),
    system: Yup.string().when('scope', {
      is: 'one',
      then: Yup.string().required(intl.formatMessage({id: 'FIELD.REQUIRED'})),
    }),
  });

  const initialValues: LoginAsCustomerFormValues = {
    scope: currentCustomer === null ? 'all' : 'one',
    client_id: currentCustomer?.id.toString() ?? '',
    system: currentCustomer?.system ?? '',
  };

  const formik: FormikProps<LoginAsCustomerFormValues> = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: validationRules,
    validateOnChange: true,
    onSubmit: (values: LoginAsCustomerFormValues) => {
      if (values.scope === 'all') {
        if (currentCustomer === null) {
          setShow(false);
          return;
        }
        clearCurrentCustomer();
        return;
      }

      if (
        currentCustomer !== null &&
        currentCustomer.id.toString() === values.client_id &&
        currentCustomer.system === values.system
      ) {
        setShow(false);
        return;
      }

      setCurrentCustomer({
        customerId: Number(values.client_id),
        system: values.system as CustomerSystem,
      });
    },
  });

  if (!isAdmin && customers.length === 0) {
    return null;
  }

  // Contact mono-client : on affiche son client, sans possibilité d'en changer
  if (!isAdmin && customers.length === 1) {
    const onlyCustomer = currentCustomer ?? customers[0];
    const onlyLabel = [onlyCustomer.id, onlyCustomer.name].filter(Boolean).join(' - ');

    return (
      <div className={className}>
        <OverlayTrigger placement="bottom" overlay={<Tooltip>{onlyLabel}</Tooltip>}>
          <span
            className="btn btn-sm btn-light d-flex align-items-center text-nowrap"
            style={{maxWidth: '280px', cursor: 'default'}}
          >
            <i className="bi bi-building fs-4 me-md-2"></i>
            <span className="d-none d-md-inline text-truncate">{onlyLabel}</span>
          </span>
        </OverlayTrigger>
      </div>
    );
  }

  const handleShow = () => {
    formik.resetForm();
    setShow(true);
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    clearCurrentCustomer();
  };

  const label =
    currentCustomer === null
      ? isAdmin
        ? intl.formatMessage({id: 'CUSTOMER_SCOPE.ALL_CUSTOMERS'})
        : intl.formatMessage({id: 'CUSTOMER_SCOPE.ALL_CUSTOMERS_COUNT'}, {count: customers.length})
      : [currentCustomer.id, currentCustomer.name].filter(Boolean).join(' - ');

  return (
    <div className={className}>
      <OverlayTrigger
        placement="bottom"
        overlay={
          <Tooltip>
            {label}
            <br />
            {intl.formatMessage({id: 'CUSTOMER_SCOPE.BUTTON_TOOLTIP'})}
          </Tooltip>
        }
      >
        <button
          type="button"
          onClick={handleShow}
          className={clsx(
            'btn btn-sm d-flex align-items-center text-nowrap',
            currentCustomer === null ? 'btn-light' : 'btn-light-primary'
          )}
          style={{maxWidth: '280px'}}
        >
          <i className="bi bi-building fs-4 me-md-2"></i>
          <span className="d-none d-md-inline text-truncate">{label}</span>
          {currentCustomer !== null && (
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip>{intl.formatMessage({id: 'CUSTOMER_SCOPE.CLEAR_TOOLTIP'})}</Tooltip>
              }
            >
              <span
                role="button"
                aria-label={intl.formatMessage({id: 'CUSTOMER_SCOPE.CLEAR_TOOLTIP'})}
                className="ms-2 d-flex align-items-center"
                onClick={handleClear}
              >
                <i className="bi bi-x-circle-fill fs-5"></i>
              </span>
            </OverlayTrigger>
          )}
          <i className="bi bi-chevron-down fs-7 ms-2"></i>
        </button>
      </OverlayTrigger>
      <LoginAsCustomerModal
        show={show}
        hide={() => setShow(false)}
        formik={formik}
        isLoading={isSetting || isClearing}
      />
    </div>
  );
};

export default CustomerScopeSwitcher;
